/**
 * 金额展示
 *
 * 财务场景的硬要求：数字用等宽字体、右对齐、千分位，避免人眼核对时串行。
 * 金额一律以「分」传入（后端存储口径），展示时由 @ilc/shared 的 formatMoney 转元，
 * 保证与后端算出的是同一个数。
 */

import { Tooltip } from 'antd';
import { centsToChinese, formatCents, formatMoney, yuanToCents } from '@ilc/shared';

export interface MoneyProps {
  /** 金额（分） */
  cents: number | null | undefined;
  /** 是否展示货币符号（表格列里列头已写明金额时可关掉，减少噪声） */
  symbol?: boolean;
  /** 加粗（价税合计等关键金额） */
  strong?: boolean;
  /** 负数标红 */
  colorNegative?: boolean;
  /** 悬浮显示中文大写，便于与票面「价税合计（大写）」核对 */
  withChinese?: boolean;
  emptyText?: string;
}

export function Money({
  cents,
  symbol = true,
  strong = false,
  colorNegative = true,
  withChinese = false,
  emptyText = '-',
}: MoneyProps): React.ReactElement {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return <span style={{ color: 'rgba(0,0,0,0.35)' }}>{emptyText}</span>;
  }

  const display = symbol ? formatMoney(cents) : formatCents(cents);
  const color = colorNegative && cents < 0 ? '#cf1322' : undefined;

  const node = (
    <span
      className="ilc-money"
      style={{ color, fontWeight: strong ? 600 : 400 }}
      // 等宽字体 + 表格数字对齐，保证多行金额小数点对齐
      title={withChinese ? centsToChinese(cents) : undefined}
    >
      {display}
    </span>
  );

  if (!withChinese) return node;
  return <Tooltip title={`大写：${centsToChinese(cents)}`}>{node}</Tooltip>;
}

/** 表格金额单元格：右对齐 + 等宽 */
export const moneyCellStyle: React.CSSProperties = {
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

/**
 * 元 → 分（表单提交用）。
 * 优先使用 @ilc/shared 的实现，保证与后端一致。
 */
export { yuanToCents };

export default Money;
